import { Box, Typography, useTheme } from "@mui/material";
import { IconType } from "react-icons/lib";

export type CupertinoSegmentOption<T = string> = {
    value: T;
    label: string;
    icon?: IconType;
    labelBelow?: string;
};

export type CupertinoSegmentPickerProps<T = string> = {
    label: string;
    options: CupertinoSegmentOption<T>[];
    selectedValue: T | null;
    onSelect: (value: T) => void;
};

export const CupertinoSegmentPicker = <T extends string | number = string>(props: CupertinoSegmentPickerProps<T>) => {
    const theme = useTheme();
    const { label, options, selectedValue, onSelect } = props;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
                variant="body2"
                sx={{
                    marginBottom: 0.5,
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                }}
            >
                {label}
            </Typography>
            <Box
                sx={{
                    display: "flex",
                    borderRadius: "7px",
                    backgroundColor: theme.palette.grey[100],
                    border: `1px solid ${theme.palette.divider}`,
                    padding: "4px",
                    gap: "4px",
                }}
            >
                {options.map((option) => {
                    const IconComponent = option.icon;
                    const isSelected = selectedValue === option.value;

                    return (
                        <Box
                            key={option.value}
                            onClick={() => onSelect(option.value)}
                            sx={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "8px 16px",
                                borderRadius: "5px",
                                backgroundColor: isSelected ? theme.palette.primary.main : "transparent",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                boxShadow: isSelected ? `0 1px 3px rgba(0,0,0,0.12)` : "none",
                                border: "1px solid transparent",
                                "&:hover": {
                                    backgroundColor: isSelected
                                        ? theme.palette.primary.dark
                                        : theme.palette.grey[200],
                                }
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 1,
                                }}
                            >
                                {IconComponent && (
                                    <IconComponent
                                        size={15}
                                        color={isSelected ? theme.palette.common.white : theme.palette.text.secondary}
                                    />
                                )}
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 600,
                                        color: isSelected ? theme.palette.common.white : theme.palette.text.secondary,
                                        textAlign: "center",
                                    }}
                                >
                                    {option.label}
                                </Typography>
                            </Box>
                            {option.labelBelow && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: isSelected
                                            ? theme.palette.common.white
                                            : theme.palette.text.secondary,
                                        fontSize: "14px",
                                        marginTop: "2px",
                                    }}
                                >
                                    {option.labelBelow}
                                </Typography>
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

